import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Repeat, ArrowRight, XCircle, Clock3, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from '../../../common/Toast/toast';
import RequestBatchChangeModal from './RequestBatchChangeModal.jsx';
import { useWaypoint } from '../../../common/waypoint/WaypointProvider.jsx';
import '../../../common/waypoint/waypoint.css';
import {
  getBatchChangeRequests,
  cancelBatchChangeRequest,
  extractBatchChangeErrorMessage,
} from '../../../../../services/BatchChangeRequestService';
import { confirm as confirmDialog } from '../../../common/Confirm/confirm';

/* `tone` is the .bcr-status-- modifier the chip wears. The old `variant`
   key named a Bootstrap colour and had no reader left once this card
   stopped rendering react-bootstrap. */
/* A batch name on a request, clickable when the request carried an id
   for it. Some rows only ever had a name -- a batch deleted since the
   request was raised, or a payload that did not populate it -- and
   those stay plain text rather than becoming a link to nothing. */
const BatchName = ({ batch, onOpen }) => {
  const id = batch?.id || batch?._id;
  const name = batch?.name || 'Unknown batch';
  if (!id) return <span className="bcr-batch-name">{name}</span>;
  return (
    <button
      type="button"
      className="bcr-batch-name wp-link"
      onClick={() => onOpen('batch', id)}
      title={`Open ${name}`}
    >
      {name}
    </button>
  );
};

const STATUS_META = {
  pending: { label: 'Pending', tone: 'pending' },
  approved: { label: 'Approved', tone: 'approved' },
  rejected: { label: 'Rejected', tone: 'rejected' },
  cancelled: { label: 'Cancelled', tone: 'cancelled' },
  reverted: { label: 'Reverted', tone: 'reverted' },
};

/**
 * Student-facing card: lets them file a new batch-change request (against
 * one of their currently-enrolled batches) and track the status of ones
 * they've already filed, with a cancel action while still pending.
 *
 * Renders in two places that are not the same shape — the 300px right
 * rail of the student dashboard and the full-width "Batch requests" tab
 * of the staff one — so `skin` picks which of the two .bcr-- skins it
 * wears. The markup is the same either way.
 */
const BatchChangeRequestsCard = ({
  studentId,
  myBatches = [],
  refreshSignal = 0,
  skin = 'feed',
}) => {
  // The nav bridge, so the batches a request names can be opened.
  const { open: openEntity } = useWaypoint();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [displayCount, setDisplayCount] = useState(2);
  const bcrRef = useRef(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await getBatchChangeRequests({ limit: 10, page: 1, studentId });
      setRequests(res.data || []);
    } catch (error) {
      console.error('Failed to load batch change requests', error);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshSignal]);

  const handleShowMoreRequests = () => {
    setDisplayCount((prev) => Math.min(prev + 2, requests.length));
  };

  const handleShowLessRequests = () => {
    setDisplayCount(2);
    if (bcrRef.current) {
      bcrRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const displayedRequests = requests.slice(0, displayCount);
  const remainingRequestsCount = Math.max(0, requests.length - displayCount);

  const handleCancel = async (id) => {
    const confirm = await confirmDialog({
      title: 'Withdraw this request?',
      text: 'You can file a new one later if you change your mind.',
      confirmButtonText: 'Yes, withdraw',
      cancelButtonText: 'Keep it',
      tone: 'danger',
    });
    if (!confirm.isConfirmed) return;

    setCancellingId(id);
    try {
      await cancelBatchChangeRequest(id);
      await load();
    } catch (error) {
      toast.error(extractBatchChangeErrorMessage(error, 'Please try again.'));
    } finally {
      setCancellingId(null);
    }
  };

  if (!studentId || (!loading && requests.length === 0 && myBatches.length === 0)) {
    return null;
  }

  return (
    <section className={`bcr bcr--${skin}`} ref={bcrRef}>
      <header className="bcr-head">
        <h2 className="bcr-title">
          <Repeat size={16} aria-hidden="true" />
          Batch change requests
        </h2>
        {myBatches.length > 0 && (
          <button
            type="button"
            className="bcr-new"
            onClick={() => setShowModal(true)}
          >
            New request
          </button>
        )}
      </header>

      {loading ? (
        <div className="bcr-loading">
          <span className="bcr-spinner" role="status" aria-label="Loading requests" />
        </div>
      ) : requests.length === 0 ? (
        <div className="bcr-empty">
          <Clock3 size={26} aria-hidden="true" />
          <p>No batch change requests yet.</p>
        </div>
      ) : (
        <>
          <ul className="bcr-list">
            {displayedRequests.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <li className="bcr-item" key={r.id}>
                  {/* Both ends of the move open the batch they name. A
                      request is about two batches, and the question it
                      raises -- what is the one I am being moved to? --
                      had no answer on this card until now. */}
                  <div className="bcr-move">
                    <div className="bcr-batch" title={r.fromBatch?.name}>
                      <span className="bcr-batch-label">Current batch</span>
                      <BatchName batch={r.fromBatch} onOpen={openEntity} />
                    </div>
                    <ArrowRight className="bcr-arrow" size={16} aria-hidden="true" />
                    <div className="bcr-batch" title={r.toBatch?.name}>
                      <span className="bcr-batch-label">Requested batch</span>
                      <BatchName batch={r.toBatch} onOpen={openEntity} />
                    </div>
                  </div>

                  <p className="bcr-meta">
                    {r.courseName || 'Course'} · Requested{' '}
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}
                  </p>

                  {r.reason && <p className="bcr-reason">"{r.reason}"</p>}

                  {r.status === 'rejected' && r.rejectionReason && (
                    <p className="bcr-note bcr-note--bad">Reason: {r.rejectionReason}</p>
                  )}

                  {r.status === 'reverted' && (
                    <p className="bcr-note">
                      This approval was undone by your coordinator — you're back in your
                      original batch.
                    </p>
                  )}

                  <div className="bcr-foot">
                    <span className={`bcr-status bcr-status--${meta.tone}`}>
                      {meta.label}
                    </span>
                    {r.status === 'pending' && (
                      <button
                        type="button"
                        className="bcr-cancel"
                        onClick={() => handleCancel(r.id)}
                        disabled={cancellingId === r.id}
                        title="Withdraw request"
                      >
                        <XCircle size={13} aria-hidden="true" />
                        {cancellingId === r.id ? 'Withdrawing…' : 'Withdraw'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {requests.length > 2 && (
            <div className="bcr-more">
              {displayCount > 2 && (
                <button type="button" onClick={handleShowLessRequests}>
                  Show less <ChevronUp size={14} aria-hidden="true" />
                </button>
              )}

              {displayCount < requests.length && (
                <button type="button" onClick={handleShowMoreRequests}>
                  Show more ({remainingRequestsCount}) <ChevronDown size={14} aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </>
      )}

      <RequestBatchChangeModal
        show={showModal}
        onClose={() => setShowModal(false)}
        myBatches={myBatches}
        studentId={studentId}
        onSubmitted={load}
      />
    </section>
  );
};

export default BatchChangeRequestsCard;
